import React from "react";
import { useTranslation } from "react-i18next";
import { Administrator } from "../../types";

interface AdministratorsSectionProps {
  administrators: Administrator[];
  currentUserId?: string;
  saving: boolean;
  onOpenPeoplePicker: () => void;
  onRemoveAdministrator: (adminId: string) => void;
}

export const AdministratorsSection: React.FC<AdministratorsSectionProps> = ({
  administrators,
  currentUserId,
  saving,
  onOpenPeoplePicker,
  onRemoveAdministrator,
}) => {
  const { t, i18n } = useTranslation('adminPage');

  return (
    <div className="setup-section">
      <h2>{t('sections.administrators.title')}</h2>
      <p className="help-text">{t('sections.administrators.description')}</p>

      <div className="status-card">
        <div className="status-content">
          <div className="admin-status-header">
            <h3 className="admin-section-title">
              {t('sections.administrators.count', { count: administrators.length })}
            </h3>
            <button
              onClick={onOpenPeoplePicker}
              disabled={saving}
              className="admin-add-btn"
            >
              {t('sections.administrators.add')}
            </button>
          </div>

          {administrators.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('sections.administrators.tableHeaders.name')}</th>
                    <th>{t('sections.administrators.tableHeaders.email')}</th>
                    <th>{t('sections.administrators.tableHeaders.addedDate')}</th>
                    <th>{t('sections.administrators.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {administrators.map((admin) => {
                    const isLastAdmin = administrators.length === 1;
                    const isCurrentUser = Boolean(currentUserId && admin.id === currentUserId);
                    const isDisabled = saving || isLastAdmin || isCurrentUser;

                    return (
                      <tr key={admin.id}>
                        <td>
                          <div className="table-name">{admin.displayName}</div>
                        </td>
                        <td>
                          <div className="table-label">{admin.email}</div>
                        </td>
                        <td>
                          <div className="table-label">
                            {new Date(admin.addedDate).toLocaleDateString(i18n.language, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => onRemoveAdministrator(admin.id)}
                            disabled={isDisabled}
                            className="admin-remove-btn"
                            title={
                              isLastAdmin
                                ? t('sections.administrators.removeTooltip.lastAdmin')
                                : isCurrentUser
                                ? t('sections.administrators.removeTooltip.currentUser')
                                : t('sections.administrators.removeTooltip.default')
                            }
                          >
                            {t('sections.administrators.remove')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="errors-section">
              <p>⚠️ {t('sections.administrators.noAdmins')}</p>
              <p className="admin-mt-8 admin-text-small">
                {t('sections.administrators.noAdminsHint')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
